import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Field,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupInput,
} from "@beweegbaas/ui";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { toast } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { LayoutAuth } from "#/layouts/layout.auth";
import { verify2FASetupSchema } from "#/modules/generic.login/schema.2fa";
import { get2FASetup, verify2FASetup } from "#/modules/util.auth/twofa.functions";
import { useAppForm } from "#/modules/util.forms/form";
import IconMaterialSymbolsCheckCircleRounded from "~icons/material-symbols/check-circle-rounded";
import IconMaterialSymbolsContentCopy from "~icons/material-symbols/content-copy";
import { z } from "zod/v4";
import { fieldErrorText } from "#/components/form-errors";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { sanitizeRedirect } from "#/modules/util.auth/sanitize-redirect";
import { useIsAdmin } from "#/modules/util.auth/use-user";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/setup-2fa")({
  // `referer` lets the change-2FA flow (account security tab) bounce the user back after setup.
  validateSearch: z.object({
    referer: z
      .string()
      .refine((path) => sanitizeRedirect(path) !== undefined)
      .optional()
      .catch(undefined),
  }),
  loader: () => get2FASetup(),
  component: Setup2FAPage,
});

function Setup2FAPage() {
  const setup = Route.useLoaderData();
  const { referer } = Route.useSearch();
  const router = useRouter();
  const isAdmin = useIsAdmin();
  const verify = useServerFn(verify2FASetup);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | undefined>(undefined);
  const [confirmContinueOpen, setConfirmContinueOpen] = useState(false);

  const form = useAppForm({
    defaultValues: { code: "" },
    validators: { onSubmit: verify2FASetupSchema },
    onSubmit: async ({ value }) => {
      const res = await verify({ data: { code: value.code } });
      if (!res) return;
      if ("formError" in res) {
        form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
        return;
      }
      // Arrived from the security settings (change-2FA)? Bounce back there once enabled. The
      // forced-2FA guard (`$school` beforeLoad) passes no referer, so it keeps the current
      // recovery-codes screen instead.
      if (referer) {
        await router.navigate({ href: referer });
        return;
      }
      setRecoveryCodes(res.recoveryCodes ?? []);
    },
  });

  // 2FA already enabled — dedicated confirmation screen (mirrors portal's `alreadyEnabled`).
  if ("alreadyEnabled" in setup) {
    return (
      <LayoutAuth>
        <title>{m["auth.setup_2fa.title_page"]()}</title>
        <Card className="relative w-full">
          <CardContent>
            <div className="flex flex-col gap-4">
              <Alert variant="default" className="border-none">
                <IconMaterialSymbolsCheckCircleRounded className="text-green-600!" />
                <AlertTitle>{m["auth.setup_2fa.already_enabled_title"]()}</AlertTitle>
                <AlertDescription>
                  {m["auth.setup_2fa.already_enabled_description"]()}
                </AlertDescription>
              </Alert>
              <Button
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/login"
                    params={(prev: Record<string, string>) => prev}
                  />
                }
                variant="secondary"
              >
                {m["common.auth.back_to_login"]()}
              </Button>
            </div>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  // Loader error — show the message with a way back to login.
  if ("formError" in setup) {
    return (
      <LayoutAuth>
        <title>{m["auth.setup_2fa.title_page"]()}</title>
        <Card className="relative w-full">
          <CardContent>
            <Alert variant="default" className="border-none">
              <AlertTitle>{m["common.errors.title"]()}</AlertTitle>
              <AlertDescription>{setup.formError}</AlertDescription>
            </Alert>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/login"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              variant="secondary"
              className="mt-4"
            >
              {m["common.auth.back_to_login"]()}
            </Button>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  // Success state — show recovery codes (only reached without a referer; the referer flow
  // navigates away above).
  if (recoveryCodes) {
    return (
      <LayoutAuth>
        <title>{m["auth.setup_2fa.success_title_page"]()}</title>
        <Card className="relative w-full">
          <CardContent>
            <div className="flex flex-col gap-4">
              <Alert variant="default" className="border-none">
                <IconMaterialSymbolsCheckCircleRounded className="text-green-600!" />
                <AlertTitle>{m["auth.setup_2fa.success_alert_title"]()}</AlertTitle>
                <AlertDescription>{m["auth.setup_2fa.success_description"]()}</AlertDescription>
              </Alert>
              <div className="bg-muted rounded-lg p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold">
                    {m["auth.setup_2fa.recovery_codes_label"]()}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async (event) => {
                      // currentTarget is nulled once the handler yields, capture it for the anchor.
                      const anchor = event.currentTarget;
                      try {
                        await navigator.clipboard.writeText(recoveryCodes.join("\n"));
                        toast.anchored(m["auth.setup_2fa.copied_to_clipboard"](), { anchor });
                      } catch (error) {
                        captureUnexpectedError(error, "clipboard");
                        toast.error(m["auth.setup_2fa.copy_failed"]());
                      }
                    }}
                  >
                    <IconMaterialSymbolsContentCopy className="size-4" />
                    {m["auth.setup_2fa.copy_codes"]()}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {recoveryCodes.map((code) => (
                    <div key={code} className="break-all">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <Button variant="success" onClick={() => setConfirmContinueOpen(true)}>
                {m["common.actions.continue"]()}
              </Button>
              <AlertDialog open={confirmContinueOpen} onOpenChange={setConfirmContinueOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {m["auth.setup_2fa.confirm_continue_title"]()}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {m["auth.setup_2fa.confirm_continue_description"]()}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel variant="outline" size="default">
                      {m["common.actions.cancel"]()}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        setConfirmContinueOpen(false);
                        void router.navigate({
                          from: Route.fullPath,
                          to: "/$locale/{-$schoolyear}/login",
                          params: (prev) => prev,
                        });
                      }}
                    >
                      {m["auth.setup_2fa.confirm_continue_button"]()}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  // Setup form — show the QR code, the manual entry key, and the verification form.
  return (
    <LayoutAuth>
      <title>{m["auth.setup_2fa.title_page"]()}</title>
      <Card className="relative w-full">
        <CardContent>
          <div className="mb-6">
            <h1 className="mb-2 text-2xl font-bold">{m["auth.setup_2fa.title"]()}</h1>
            <p className="text-muted-foreground">
              {isAdmin
                ? m["auth.setup_2fa.description_admin_requires_2fa"]()
                : m["auth.setup_2fa.description_one_or_more_schools_requires_2fa"]()}
            </p>
          </div>

          <div className="mb-6 flex flex-col gap-6">
            {/* QR Code */}
            <div
              className="from-muted to-muted/20 relative flex
                w-full flex-col items-center justify-center gap-2 rounded-lg
                bg-linear-to-br p-4"
            >
              <span className="text-muted-foreground self-start font-mono text-xs">optie 1</span>
              {setup.qrCodeDataUrl ? (
                <img
                  src={setup.qrCodeDataUrl}
                  alt={m["auth.setup_2fa.qr_code_alt"]()}
                  className="squircle size-[200px]
                    rounded-lg shadow-xl supports-squircle:rounded-xl"
                />
              ) : (
                <div
                  className="text-muted-foreground flex h-[200px] w-[200px]
                    items-center justify-center"
                >
                  {m["auth.setup_2fa.qr_code_loading"]()}
                </div>
              )}

              <p
                className="font-heading text-muted-foreground m-0 text-center
                  text-sm"
              >
                {m["auth.setup_2fa.scan_instruction"]()}
              </p>
            </div>

            {/* Manual entry key */}
            <div
              className="from-muted to-muted/20 relative flex flex-col
                gap-2 rounded-lg bg-linear-to-br p-4"
            >
              <span className="text-muted-foreground block font-mono text-xs">optie 2</span>
              <p className="m-0 text-sm font-semibold">
                {m["auth.setup_2fa.manual_entry_title"]()}
              </p>
              <p className="text-muted-foreground m-0 text-sm">
                {m["auth.setup_2fa.manual_entry_description"]()}
              </p>
              <div className="flex items-center gap-2">
                <p className="flex-1 font-mono text-sm break-all">{setup.sharedKey}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async (event) => {
                    const anchor = event.currentTarget;
                    try {
                      await navigator.clipboard.writeText(setup.sharedKey);
                      toast.anchored(m["auth.setup_2fa.setup_code_copied_to_clipboard"](), {
                        anchor,
                      });
                    } catch (error) {
                      captureUnexpectedError(error, "clipboard");
                      toast.error(m["auth.setup_2fa.copy_failed"]());
                    }
                  }}
                  className="shrink-0"
                  aria-label={m["auth.setup_2fa.copy_codes"]()}
                >
                  <IconMaterialSymbolsContentCopy className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          <form.AppForm>
            <form.FormRoot className="flex max-w-lg flex-col gap-4">
              <form.AppField
                name="code"
                children={(field) => {
                  const hasError = field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={hasError || undefined}>
                      <FieldLabel htmlFor="code">
                        {m["auth.setup_2fa.verification_code_label"]()}
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupInput
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          id="code"
                          name={field.name}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="one-time-code"
                          placeholder="000000"
                          maxLength={6}
                          aria-invalid={hasError || undefined}
                        />
                      </InputGroup>
                      {hasError && (
                        <FieldError>{fieldErrorText(field.state.meta.errors)}</FieldError>
                      )}
                    </Field>
                  );
                }}
              />

              <form.SubmitButton>{m["auth.setup_2fa.submit_button"]()}</form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
